import { isAnswered, listeningParts, trackGroups } from '../../constants';
import type { CandidateQuestion, CandidateSection } from '../../types';

export interface ReviewItem {
  id: string;
  number: number;
  answered: boolean;
}

export interface ReviewGroup {
  key: string;
  kind: 'listening' | 'reading' | 'writing' | 'questions';
  /** 1-based part, passage or task. */
  index: number;
  items: ReviewItem[];
}

export interface SectionReview {
  groups: ReviewGroup[];
  answered: number;
  unanswered: number;
}

/** Every sub-question id of a Reading passage, across its pages. */
export const subQuestionIds = (passage: CandidateQuestion): string[] => {
  const pages = (passage.pages ?? []) as Array<{ subQuestions?: Array<{ id: string }> }>;
  const nested = pages.flatMap((page) => page.subQuestions ?? []);
  return [...(passage.subQuestions ?? []), ...nested].map((q: { id: string }) => q.id);
};

/**
 * Every question of the open section, grouped like the paper (Listening parts,
 * Reading passages, Writing tasks) and numbered straight through, for the
 * review screen shown before a section is closed.
 */
export function buildSectionReview(section: CandidateSection, answers: Map<string, unknown>): SectionReview {
  let number = 0;
  const item = (id: string, answered: boolean): ReviewItem => ({ id, number: ++number, answered });
  let groups: ReviewGroup[];

  if (section.kind === 'IELTS_LISTENING') {
    const tracks = section.stimulus?.tracks ?? [];
    const grouping = trackGroups(tracks);
    const groupCount = grouping.length ? grouping[grouping.length - 1] + 1 : 0;
    groups = listeningParts(section.questions, tracks.length, section.stimulus?.questionsPerPart, groupCount)
      .filter((part) => part.length)
      .map((part, i) => ({
        key: `l${i}`,
        kind: 'listening',
        index: i + 1,
        items: part.map((q) => item(q.id, isAnswered(answers.get(q.id)))),
      }));
  } else if (section.kind === 'IELTS_READING') {
    const passages = section.questions.filter((q) => q.type === 'PASSAGE');
    const others = section.questions.filter((q) => q.type !== 'PASSAGE');
    groups = passages.map((passage, i) => {
      const given = (answers.get(passage.id) ?? {}) as Record<string, unknown>;
      return {
        key: passage.id,
        kind: 'reading',
        index: i + 1,
        items: subQuestionIds(passage).map((id) => item(id, isAnswered(given[id]))),
      };
    });
    if (others.length) {
      groups.push({
        key: 'other',
        kind: 'questions',
        index: groups.length + 1,
        items: others.map((q) => item(q.id, isAnswered(answers.get(q.id)))),
      });
    }
  } else if (section.kind === 'IELTS_WRITING') {
    groups = section.questions.map((q, i) => ({
      key: q.id,
      kind: 'writing',
      index: i + 1,
      items: [item(q.id, isAnswered(answers.get(q.id)))],
    }));
  } else {
    groups = [{ key: 'all', kind: 'questions', index: 1, items: section.questions.map((q) => item(q.id, isAnswered(answers.get(q.id)))) }];
  }

  const all = groups.flatMap((g) => g.items);
  const answered = all.filter((i) => i.answered).length;
  return { groups: groups.filter((g) => g.items.length), answered, unanswered: all.length - answered };
}
